/**
 * AI 주간 보고서
 * ---------------------------------------------------------------------------
 * 고른 기간의 운영일지를 AI 가 읽고 주간 보고서를 써 준다.
 *
 * ## 누르기 전에 비용을 보여 준다
 * 1건당 요금이 청구서에 붙는다. 「생성」을 누른 뒤에 "얼마 나왔습니다" 를
 * 보여 주면 늦다. 단추 위에 **1건 얼마인지** 적어 두고, 실패하면 청구하지
 * 않는다(플랫폼이 호출에 성공한 뒤에만 건수를 적는다).
 *
 * ## 기간은 1개월까지
 * 진짜로 막는 쪽은 플랫폼이다. 여기서 또 막는 이유는 **거절당하기 전에
 * 알려 주기 위해서**다 — 날짜를 고르고 누른 다음 빨간 글씨를 보는 것보다,
 * 고를 수 없는 날짜가 회색인 편이 낫다.
 *
 * 왜 1개월인가: 자료가 많을수록 요약이 뭉개지고 비용도 늘어난다. 석 달치를
 * 한 번에 넣으면 "대체로 양호합니다" 같은 글이 나온다.
 */

import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntApp,
  Button,
  Checkbox,
  DatePicker,
  Modal,
  Result,
  Space,
  Spin,
  Typography,
} from 'antd';
import { DownloadOutlined, RobotOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { api, errorMessage } from '../lib/api';
import { ReportDocument } from './ReportDocument';
import { ReportCharts, type TrendSeries } from './ReportCharts';
import { downloadReportPdf, safeFileName } from '../lib/reportPdf';

const { Paragraph, Text } = Typography;

interface AiConfig {
  enabled: boolean;
  provider: string;
  providerName: string;
  model: string | null;
  pricePerReport: number | null;
  maxInputChars: number;
  disabledReason: string | null;
  /** 이번 달 무료 건수 — 기업 단위다(사람 수와 무관) */
  freePerMonth: number;
  usedThisMonth: number;
  freeRemaining: number;
  periodStart: string;
  periodEnd: string;
  /** 동의 문구는 Hub 가 준다 — 세 제품이 같은 문장을 띄워야 한다 */
  consentText: string;
}

/**
 * 이번 달에 몇 건을 만들었고, 무료는 월 몇 건인가.
 *
 * ## 한 줄로 끝낸다
 * 처음에는 「무료 N건 중 M건 사용 · 남은 무료 K건 · 초과 시 1건 ○○○원」에
 * 설명 문단까지 붙여 두었다. 창을 열자마자 읽을 것이 너무 많아 정작
 * 고르려던 기간이 아래로 밀렸다. 쓰는 사람이 알아야 할 것은 두 가지뿐이다 —
 * <b>이번 달에 몇 건 썼나</b>, <b>무료는 몇 건까지인가.</b> 돈이 붙는
 * 시점은 아래 안내에서 한 번 더 말한다.
 */
/** `2026-10-01` → `2026년 10월`. 과금 기간의 첫날을 사람이 읽는 달 이름으로. */
function monthLabel(periodStart: string | null | undefined) {
  return periodStart
    ? `${periodStart.slice(0, 4)}년 ${Number(periodStart.slice(5, 7))}월`
    : '이번 달';
}

function QuotaLine({ config }: { config: AiConfig }) {
  const month = monthLabel(config.periodStart);

  return (
    <Alert
      type={config.freeRemaining > 0 ? 'success' : 'warning'}
      showIcon
      style={{ padding: '6px 12px' }}
      message={
        <Text>
          {month}: 보고서 생성 <b>{config.usedThisMonth}건</b>
          <Text type="secondary">(무료 제공 {config.freePerMonth}건/월)</Text>
        </Text>
      }
    />
  );
}

/**
 * 화면 설명 안에 들어가는 <b>살아 있는</b> 사용량 한 줄.
 *
 * ## 왜 숫자를 적어 두지 않나
 * 설명에 「무료 제공 5건/월」처럼 숫자를 박아 두면, 요금제가 바뀌는 순간
 * <b>설명만 거짓말이 된다.</b> 실제로 그랬다 — 무료 수량을 내린 뒤에도 설명은
 * 예전 숫자를 말하고 있었다. 돈에 관한 숫자라 더 나쁘다.
 *
 * 그래서 {@link QuotaLine} 과 <b>같은 값·같은 서식</b>을 요금제에서 읽어 와
 * 그대로 보여 준다. 설명과 화면이 어긋날 수 없다.
 *
 * 질의 키가 창의 것과 같아 이미 읽어 둔 값이 있으면 그대로 쓴다.
 */
export function AiQuotaExample() {
  const { data, isLoading } = useQuery<AiConfig>({
    queryKey: ['ai-report-config'],
    queryFn: async () => (await api.get<AiConfig>('/ai-reports/config')).data,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) return <b>「…」</b>;
  if (!data?.enabled) return <b>「이번 달: 보고서 생성 ○건(무료 제공 ○건/월)」</b>;

  return (
    <b>
      「{monthLabel(data.periodStart)}: 보고서 생성 {data.usedThisMonth}건 (무료 제공{' '}
      {data.freePerMonth}건/월)」
    </b>
  );
}

interface AiReportResult {
  text: string;
  providerName: string;
  pricePerReport: number;
  logCount: number;
  /** 서버가 원장에서 뽑은 차트용 수치. 본문의 표와 같은 숫자다 */
  series: TrendSeries[];
}

export function AiReportButton({ siteId }: { siteId?: string }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button icon={<RobotOutlined />} onClick={() => setOpen(true)}>
        AI 주간 보고서
      </Button>
      {/* 열 때 비로소 설정을 불러온다 — 쓰지 않는 사람에게 매번 호출할 이유가 없다. */}
      {open && <AiReportDialog siteId={siteId} onClose={() => setOpen(false)} />}
    </>
  );
}

function AiReportDialog({ siteId, onClose }: { siteId?: string; onClose: () => void }) {
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();

  // 기본값은 지난 7일. "주간" 보고서라서다.
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(6, 'day'), dayjs()]);
  const [result, setResult] = useState<AiReportResult | null>(null);
  /*
   * 동의는 **만들 때마다** 새로 받는다. 한 번 체크해 두고 계속 쓰게 하면
   * 「그때 동의했다」 가 아니라 「예전에 한 번 체크했다」 가 된다.
   */
  const [consent, setConsent] = useState(false);

  const config = useQuery<AiConfig>({
    queryKey: ['ai-report-config'],
    queryFn: async () => (await api.get<AiConfig>('/ai-reports/config')).data,
    staleTime: 5 * 60 * 1000,
  });

  const generate = useMutation({
    mutationFn: async () =>
      (
        await api.post<AiReportResult>('/ai-reports', null, {
          params: {
            siteId,
            periodStart: range[0].format('YYYY-MM-DD'),
            periodEnd: range[1].format('YYYY-MM-DD'),
            consent: true,
          },
        })
      ).data,
    onSuccess: (data) => {
      setResult(data);
      /*
       * 사용 건수가 하나 늘었다. 다시 읽지 않으면 「무료 5건 중 2건 사용」이
       * 그대로 남아, 다음에 누를 때 돈이 나가는지를 틀리게 보여 준다.
       */
      void queryClient.invalidateQueries({ queryKey: ['ai-report-config'] });
      void queryClient.invalidateQueries({ queryKey: ['ai-reports'] });
      setConsent(false);
    },
    onError: (err) => message.error(errorMessage(err)),
  });

  const price = config.data?.pricePerReport;
  const days = range[1].diff(range[0], 'day') + 1;


  return (
    <Modal
      open
      width={820}
      title="AI 주간 보고서"
      onCancel={onClose}
      maskClosable={!generate.isPending}
      footer={
        result
          ? [
              <Button key="again" onClick={() => setResult(null)}>
                다시 만들기
              </Button>,
              <Button
                key="copy"
                type="primary"
                onClick={() => {
                  navigator.clipboard
                    .writeText(result.text)
                    .then(() => message.success('복사했습니다'))
                    .catch(() => message.error('복사하지 못했습니다. 글을 직접 선택해 주세요.'));
                }}
              >
                복사
              </Button>,
            ]
          : [
              <Button key="cancel" onClick={onClose} disabled={generate.isPending}>
                닫기
              </Button>,
              <Button
                key="go"
                type="primary"
                icon={<RobotOutlined />}
                loading={generate.isPending}
                /* 동의 없이는 누를 수 없다. Hub 도 같은 이유로 거절한다. */
                disabled={!config.data?.enabled || !consent}
                onClick={() => generate.mutate()}
              >
                보고서 만들기
              </Button>,
            ]
      }
    >
      {config.isLoading ? (
        <Spin />
      ) : result ? (
        <ReportView result={result} />
      ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {!config.data?.enabled && (
            <Alert
              type="warning"
              showIcon
              message="지금은 쓸 수 없습니다"
              description={config.data?.disabledReason ?? 'AI 보고서를 쓸 수 없는 상태입니다.'}
            />
          )}

          {config.data?.enabled && <QuotaLine config={config.data} />}

          <div>
            <Text strong>분석 기간</Text>
            <div style={{ marginTop: 8 }}>
              <DatePicker.RangePicker
                value={range}
                allowClear={false}
                onChange={(v) => {
                  if (v?.[0] && v[1]) setRange([v[0], v[1]]);
                }}
                /*
                  오늘 이후는 고를 수 없고, 한쪽을 집은 뒤에는 그 날로부터
                  1개월 밖이 닫힌다. 거절당하고 나서 알게 하지 않는다.
                */
                disabledDate={(d, info) => {
                  if (d.isAfter(dayjs(), 'day')) return true;
                  if (!info.from) return false;
                  // 플랫폼과 같은 셈이다(달 단위). 30일로 어림잡으면 2월에 어긋난다.
                  return d.isAfter(info.from)
                    ? d.isAfter(info.from.add(1, 'month'), 'day')
                    : d.isBefore(info.from.subtract(1, 'month'), 'day');
                }}
              />
              <Text type="secondary" style={{ marginLeft: 12 }}>
                {days}일
              </Text>
            </div>
            <Text type="secondary" style={{ fontSize: 13 }}>
              최대 1개월까지 분석 가능
            </Text>
          </div>

          {config.data?.enabled && (
            <Alert
              type="info"
              showIcon
              style={{ padding: '6px 12px' }}
              message={
                <>
                  이 기간의 운영일지를 AI 가 읽고 작성 현황·미확인 일지·이상 사항·다음 주 확인할
                  것을 정리합니다.{' '}
                  <b>
                    무료 수량 초과 시 건당 비용이 발생합니다.
                    {config.data.pricePerReport !== null &&
                      `(${config.data.pricePerReport.toLocaleString()}원/건)`}
                  </b>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    자료가 {config.data.maxInputChars.toLocaleString()}자를 넘으면 뒷부분은 분석에서
                    빠집니다. 그럴 때는 기간을 나누어 만들어 주세요.
                  </Text>
                </>
              }
            />
          )}

          {config.data?.enabled && (
            <Checkbox
              checked={consent}
              disabled={generate.isPending}
              onChange={(e) => setConsent(e.target.checked)}
            >
              {config.data.consentText}
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  선택된 모델: <b>{config.data.providerName}</b> · 동의 여부는 보고서에 함께
                  기록됩니다.
                </Text>
              </div>
            </Checkbox>
          )}

          {generate.isPending && (
            <Result
              icon={<Spin size="large" />}
              title="보고서를 만들고 있습니다"
              subTitle="보통 20~60초 걸립니다. 이 창을 닫지 말아 주세요."
            />
          )}
        </Space>
      )}
    </Modal>
  );
}

/**
 * 결과.
 *
 * 마크다운을 그리는 라이브러리를 들이지 않았다. 글 하나 보여 주자고 번들을
 * 키울 이유가 없고, 복사해서 붙일 곳(메일·한글)에서는 어차피 원문이 낫다.
 */
function ReportView({ result }: { result: AiReportResult }) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [saving, setSaving] = useState(false);
  const { message } = AntApp.useApp();

  const onDownload = async () => {
    if (!bodyRef.current) return;
    setSaving(true);
    try {
      await downloadReportPdf({
        source: bodyRef.current,
        fileName: safeFileName(`AI보고서_${new Date().toISOString().slice(0, 10)}`),
      });
    } catch (e) {
      message.error(`PDF 를 만들지 못했습니다: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      {/*
        결과 보고서에는 **엔진도 금액도 적지 않는다.**
        받아서 윗선에 올리는 글이다. 거기에 「Claude · 350원 청구」가
        붙어 있으면 보고서가 아니라 영수증으로 읽힌다. 어떤 엔진으로
        만들었는지는 보관함 기록에, 금액은 청구서에 남는다.
      */}
      <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
        <Text type="secondary">운영일지 {result.logCount}건을 분석했습니다.</Text>
        <Button size="small" icon={<DownloadOutlined />} loading={saving} onClick={onDownload}>
          PDF 다운로드
        </Button>
      </Space>
      {/* ref 는 **PDF 로 찍을 범위**다 — 본문과 차트가 함께 들어가야 한다 */}
      <div
        ref={bodyRef}
        style={{
          maxHeight: '55vh',
          overflowY: 'auto',
          background: '#fff',
          border: '1px solid #f0f0f0',
          borderRadius: 8,
          padding: '20px 24px',
        }}
      >
        <ReportDocument body={result.text} />
        {result.series?.length > 0 && (
          <div style={{ marginTop: 22 }}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                margin: '0 0 12px',
                paddingLeft: 9,
                borderLeft: '4px solid #1677ff',
              }}
            >
              수치 변화
            </div>
            <ReportCharts series={result.series} />
          </div>
        )}
      </div>
    </Space>
  );
}
